import { useInSessionSchedulingEnabled } from '@web-app/utils/feature-flags/in-session-scheduling';
import { useMealPlanInSessionEnabled } from '@web-app/utils/feature-flags/meal-plan-in-session';
import { useIsPrescriberFlow } from '@web-app/utils/feature-flags/prescriber-flow';
import { useMemo } from 'react';

export enum SessionPrepStepId {
  MEDICARE_BRIDGE = 'medicareBridge',
  GOALS = 'goals',
  SCHEDULE = 'schedule',
  MEAL_PLAN = 'mealPlan',
}

export interface StepConfig {
  isVisible: boolean;
  stepNumber: number | undefined;
}

export interface SessionPrepStepConfig {
  medicareBridge: StepConfig;
  goals: StepConfig;
  schedule: StepConfig;
  mealPlan: StepConfig;
}

export interface SessionPrepStepConfigInput {
  /** True when a Daily room id is available (the call is in a known room). */
  hasRoomId: boolean;
  /** True while the previous-session recap is still being fetched. */
  isRecapLoading: boolean;
  /** True once the previous-session recap has loaded. */
  hasSessionRecap: boolean;
  /**
   * True when this patient has an open Medicare Bridge case the provider can
   * still act on. Ruled-out identification is excluded. An empty checklist
   * still counts; a missing or closed case hides the step.
   */
  hasOpenBridgeCase?: boolean;
  /**
   * True while the Bridge case query has not settled. Step numbers stay
   * unassigned until then, so Goals and Schedule do not render a number that
   * shifts once an open case arrives and becomes step 1.
   */
  isBridgeCaseLoading?: boolean;
}

/**
 * Computes the visible Session Prep steps and their numbering.
 *
 * Step numbering is only applied when the in-session scheduling flag is on
 * (otherwise the tab renders flat, without "Step N" headers). In prescriber
 * flow the SMART goals step is hidden and the remaining steps shift up.
 *
 * `mealPlan.isVisible` accounts for the meal-plan flag, the room being
 * available, and the recap being loaded (or in flight, so the skeleton
 * can render). The consumer still controls the skeleton-vs-card swap.
 *
 * Bridge membership is unknown while its query is loading. Step numbers
 * stay unset for that interval so later steps do not claim a number that
 * will change when an open case is inserted at the front.
 */
export const useSessionPrepStepConfig = ({
  hasRoomId,
  isRecapLoading,
  hasSessionRecap,
  hasOpenBridgeCase = false,
  isBridgeCaseLoading = false,
}: SessionPrepStepConfigInput): SessionPrepStepConfig => {
  const isSchedulingEnabled = useInSessionSchedulingEnabled();
  const isPrescriberFlow = useIsPrescriberFlow();
  const isMealPlanEnabled = useMealPlanInSessionEnabled();

  return useMemo(() => {
    // Visual order, top to bottom. Filtering here drives both visibility
    // and the resulting step numbers — adding a step is a one-line change.
    const visibleSteps: SessionPrepStepId[] = [
      ...(hasOpenBridgeCase ? [SessionPrepStepId.MEDICARE_BRIDGE] : []),
      ...(isPrescriberFlow ? [] : [SessionPrepStepId.GOALS]),
      SessionPrepStepId.SCHEDULE,
      SessionPrepStepId.MEAL_PLAN,
    ];

    const stepNumberOf = (id: SessionPrepStepId): number | undefined => {
      // Loading is not "no case". Numbering that assumes Bridge is absent
      // jumps as soon as an open case arrives.
      if (!isSchedulingEnabled || isBridgeCaseLoading) return undefined;
      const idx = visibleSteps.indexOf(id);
      return idx === -1 ? undefined : idx + 1;
    };

    const isMealPlanVisible =
      isMealPlanEnabled && hasRoomId && (isRecapLoading || hasSessionRecap);

    return {
      medicareBridge: {
        isVisible: hasOpenBridgeCase,
        stepNumber: stepNumberOf(SessionPrepStepId.MEDICARE_BRIDGE),
      },
      goals: {
        isVisible: !isPrescriberFlow,
        stepNumber: stepNumberOf(SessionPrepStepId.GOALS),
      },
      schedule: {
        isVisible: isSchedulingEnabled,
        stepNumber: stepNumberOf(SessionPrepStepId.SCHEDULE),
      },
      mealPlan: {
        isVisible: isMealPlanVisible,
        stepNumber: stepNumberOf(SessionPrepStepId.MEAL_PLAN),
      },
    };
  }, [
    isSchedulingEnabled,
    isPrescriberFlow,
    isMealPlanEnabled,
    hasRoomId,
    isRecapLoading,
    hasSessionRecap,
    hasOpenBridgeCase,
    isBridgeCaseLoading,
  ]);
};
